const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const REVIEWS_API_URL = `${API_BASE_URL}/api/reviews`;

const getErrorMessage = async (response, fallbackMessage) => {
  try {
    const errorData = await response.json();
    return errorData.message || fallbackMessage;
  } catch (error) {
    return fallbackMessage;
  }
};

const buildReviewPayload = (reviewData) => ({
  title: (reviewData.title || '').trim(),
  text: reviewData.text || 'Interview notes coming soon.',
  companyName: (reviewData.companyName || '').trim(),
  role: (reviewData.role || '').trim(),
  location: (reviewData.location || '').trim(),
  anonymous: Boolean(reviewData.anonymous),
  interviewDetails: {
    interviewDate: reviewData.interviewDate || '',
    experienceLevel: reviewData.experienceLevel || '',
    difficulty: reviewData.difficulty || '',
    outcome: reviewData.outcome || '',
    compensation: reviewData.compensation || '',
    applicationSource: reviewData.applicationSource || '',
    preparationTime: reviewData.preparationTime || '',
    rounds: reviewData.rounds || '',
    questions: reviewData.questions || '',
    tips: reviewData.tips || '',
  },
});

const fetchReviews = async (queryParams = '') => {
  try {
    const response = await fetch(`${REVIEWS_API_URL}?${queryParams}`);
    if (!response.ok) {
      throw new Error(await getErrorMessage(response, 'Failed to fetch interviews.'));
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching reviews:", error);
    throw error;
  }
};

const createReview = async (reviewData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${REVIEWS_API_URL}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(buildReviewPayload(reviewData)),
    });

    if (!response.ok) {
      throw new Error(await getErrorMessage(response, 'Failed to create interview.'));
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating review:", error);
    throw error;
  }
};

const fetchArticleById = async (id) => {
  try {
    const response = await fetch(`${REVIEWS_API_URL}/${id}`);
    if (!response.ok) {
      throw new Error(await getErrorMessage(response, `Failed to fetch interview ${id}.`));
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching review with ID ${id}:`, error);
    throw error;
  }
};

const updateReview = async (id, reviewData) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  try {
    const response = await fetch(`${REVIEWS_API_URL}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(buildReviewPayload(reviewData)),
    });

    if (!response.ok) {
      throw new Error(await getErrorMessage(response, `Failed to update interview ${id}.`));
    }
    return await response.json();
  } catch (error) {
    console.error(`Error updating review with ID ${id}:`, error);
    throw error;
  }
};

const deleteArticle = async (id) => {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const response = await fetch(`${REVIEWS_API_URL}/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await getErrorMessage(response, `Failed to delete interview ${id}.`));
    }
    return 'Review deleted successfully';
  } catch (error) {
    console.error(`Error deleting review with ID ${id}:`, error);
    throw error;
  }
};

const fetchLikesForArticle = async (articleId, token = null) => {
  try {
    const url = `${REVIEWS_API_URL}/${articleId}/likes`;
    const options = token ? {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    } : {};

    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(await getErrorMessage(response, 'Failed to fetch likes.'));
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch likes for article:', error);
    throw error;
  }
};

const addLikeToArticle = async (articleId) => {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const url = `${REVIEWS_API_URL}/${articleId}/likes`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(await getErrorMessage(response, 'Failed to add like.'));
    }
  } catch (error) {
    console.error('Failed to add like to article', error);
    throw error;
  }
};

const removeLikeFromArticle = async (articleId) => {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const url = `${REVIEWS_API_URL}/${articleId}/likes`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(await getErrorMessage(response, 'Failed to remove like.'));
    }
  } catch (error) {
    console.error('Failed to delete like from article', error);
    throw error;
  }
};

module.exports = {
  fetchReviews,
  createReview,
  fetchArticleById,
  updateReview,
  deleteArticle,
  fetchLikesForArticle,
  addLikeToArticle,
  removeLikeFromArticle
};
